import type { AuditTemplate } from "./templates";

export type SavedEntry = Record<string, string | number | boolean>;

export type AuditRecord = {
  id: string;
  title: string;
  site: string;
  assignmentId?: string;
  templateId: string;
  risk: "Low" | "Medium" | "High" | "Critical";
  status: "Draft" | "In progress" | "Ready for review" | "Closed";
  updatedAt: string;
  savedAt?: string;
  values: SavedEntry;
};

export type AuditorProfile = {
  id: string;
  label: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  savedAt?: string;
};

export type AuditAssignment = {
  id: string;
  label: string;
  client: string;
  country: string;
  portTerminal: string;
  vessel: string;
  auditorProfileId: string;
  auditorProfileIds: string[];
  areaAuditorMap: Record<string, string>;
  scope: string;
  startDate: string;
  endDate: string;
  status: "Draft" | "In progress" | "Completed" | "Closed";
  savedAt?: string;
  updatedAt: string;
};

export type AppLanguage = "en" | "it";
export type RepositoryProvider = "google-drive" | "dropbox" | "onedrive" | "mega" | "other";
export type RepositoryStatus = "Not connected" | "Needs configuration" | "Connected";

export type ExternalRepository = {
  id: string;
  label: string;
  provider: RepositoryProvider;
  status: RepositoryStatus;
  rootFolder: string;
  accountEmail: string;
  clientId: string;
  savedAt?: string;
};

export type AppSettings = {
  language: AppLanguage;
  cloudMode: "local-first" | "cloud-online";
  authMode: "cloudflare-access" | "app-login";
  primaryRepositoryId: string;
  repositories: ExternalRepository[];
};

const STORAGE_KEY = "wn.audit.records";
const AUDITOR_KEY = "wn.audit.auditor";
const AUDITORS_KEY = "wn.audit.auditors";
const ASSIGNMENTS_KEY = "wn.audit.assignments";
const SETTINGS_KEY = "wn.audit.settings";

export function loadAppSettings(): AppSettings {
  const raw = localStorage.getItem(SETTINGS_KEY);
  if (!raw) return defaultAppSettings();
  try {
    const parsed = JSON.parse(raw) as Partial<AppSettings>;
    const repositories = Array.isArray(parsed.repositories) && parsed.repositories.length ? parsed.repositories.map(normalizeRepository) : [newExternalRepository("google-drive")];
    return {
      language: parsed.language === "it" ? "it" : "en",
      cloudMode: parsed.cloudMode === "cloud-online" ? "cloud-online" : "local-first",
      authMode: parsed.authMode === "app-login" ? "app-login" : "cloudflare-access",
      primaryRepositoryId: parsed.primaryRepositoryId || repositories[0]?.id || "",
      repositories
    };
  } catch {
    return defaultAppSettings();
  }
}

export function saveAppSettings(settings: AppSettings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function newExternalRepository(provider: RepositoryProvider = "google-drive"): ExternalRepository {
  return {
    id: crypto.randomUUID(),
    label: repositoryProviderLabel(provider),
    provider,
    status: "Not connected",
    rootFolder: "Windward Nexus Audit Evidence",
    accountEmail: "",
    clientId: ""
  };
}

export function repositoryProviderLabel(provider: RepositoryProvider) {
  const labels: Record<RepositoryProvider, string> = {
    "google-drive": "Google Drive",
    dropbox: "Dropbox",
    onedrive: "OneDrive",
    mega: "MEGA",
    other: "Other repository"
  };
  return labels[provider];
}

function defaultAppSettings(): AppSettings {
  const repository = newExternalRepository("google-drive");
  return {
    language: "en",
    cloudMode: "local-first",
    authMode: "cloudflare-access",
    primaryRepositoryId: repository.id,
    repositories: [repository]
  };
}

function normalizeRepository(repository: Partial<ExternalRepository>): ExternalRepository {
  const provider = ["google-drive", "dropbox", "onedrive", "mega", "other"].includes(String(repository.provider)) ? (repository.provider as RepositoryProvider) : "google-drive";
  return {
    ...newExternalRepository(provider),
    ...repository,
    id: repository.id || crypto.randomUUID(),
    label: repository.label || repositoryProviderLabel(provider),
    provider,
    status: repository.status === "Connected" || repository.status === "Needs configuration" ? repository.status : "Not connected",
    rootFolder: repository.rootFolder || "Windward Nexus Audit Evidence",
    accountEmail: repository.accountEmail || "",
    clientId: repository.clientId || ""
  };
}

export function loadAudits(): AuditRecord[] {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    return JSON.parse(raw) as AuditRecord[];
  } catch {
    return [];
  }
}

export function saveAudits(audits: AuditRecord[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(audits));
}

export function loadAuditorProfile(): AuditorProfile {
  return loadAuditorProfiles()[0];
}

export function saveAuditorProfile(profile: AuditorProfile) {
  localStorage.setItem(AUDITOR_KEY, JSON.stringify(profile));
}

export function newAuditorProfile(): AuditorProfile {
  return { id: crypto.randomUUID(), label: "Auditor", firstName: "", lastName: "", phone: "", email: "" };
}

export function loadAuditorProfiles(): AuditorProfile[] {
  const raw = localStorage.getItem(AUDITORS_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as AuditorProfile[];
      if (parsed.length) return parsed;
    } catch {
      // Fall through to legacy single profile.
    }
  }
  const legacy = localStorage.getItem(AUDITOR_KEY);
  if (legacy) {
    try {
      const parsed = JSON.parse(legacy) as Partial<AuditorProfile>;
      return [{ ...newAuditorProfile(), ...parsed, id: parsed.id || crypto.randomUUID(), label: parsed.label || "Auditor" }];
    } catch {
      return [newAuditorProfile()];
    }
  }
  return [newAuditorProfile()];
}

export function saveAuditorProfiles(profiles: AuditorProfile[]) {
  localStorage.setItem(AUDITORS_KEY, JSON.stringify(profiles));
  if (profiles[0]) saveAuditorProfile(profiles[0]);
}

export function newAssignment(auditorProfileId = ""): AuditAssignment {
  return {
    id: crypto.randomUUID(),
    label: "New audit assignment",
    client: "",
    country: "",
    portTerminal: "",
    vessel: "",
    auditorProfileId,
    auditorProfileIds: auditorProfileId ? [auditorProfileId] : [],
    areaAuditorMap: {},
    scope: "",
    startDate: "",
    endDate: "",
    status: "Draft",
    updatedAt: new Date().toISOString()
  };
}

export function loadAssignments(defaultAuditorId = ""): AuditAssignment[] {
  const raw = localStorage.getItem(ASSIGNMENTS_KEY);
  if (!raw) return [newAssignment(defaultAuditorId)];
  try {
    const parsed = JSON.parse(raw) as Partial<AuditAssignment>[];
    const normalized = parsed.map((assignment) => ({
      ...newAssignment(defaultAuditorId),
      ...assignment,
      id: assignment.id || crypto.randomUUID(),
      label: assignment.label || "Audit assignment",
      auditorProfileId: assignment.auditorProfileId || assignment.auditorProfileIds?.[0] || defaultAuditorId,
      auditorProfileIds: assignment.auditorProfileIds?.length ? assignment.auditorProfileIds : [assignment.auditorProfileId || defaultAuditorId].filter(Boolean),
      areaAuditorMap: assignment.areaAuditorMap || {},
      status: assignment.status || "Draft",
      updatedAt: assignment.updatedAt || new Date().toISOString()
    }));
    return normalized.length ? normalized : [newAssignment(defaultAuditorId)];
  } catch {
    return [newAssignment(defaultAuditorId)];
  }
}

export function saveAssignments(assignments: AuditAssignment[]) {
  localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(assignments));
}

export function newAudit(template: AuditTemplate, assignment?: AuditAssignment): AuditRecord {
  return {
    id: crypto.randomUUID(),
    title: template.title,
    site: assignment?.portTerminal ?? "",
    assignmentId: assignment?.id,
    templateId: template.id,
    risk: "Medium",
    status: "Draft",
    updatedAt: new Date().toISOString(),
    values: assignment
      ? {
          assignment_label: assignment.label,
          client: assignment.client,
          country: assignment.country,
          port_terminal: assignment.portTerminal,
          vessel: assignment.vessel,
          scope: assignment.scope,
          inspection_start: assignment.startDate,
          inspection_end: assignment.endDate,
          auditor_profile_id: assignment.auditorProfileId
        }
      : {}
  };
}

export async function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    await navigator.serviceWorker.register("/sw.js");
  }
}
